import React from 'react';
import { useFormContext, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DEFAULT_DEPENDENTS_FORM } from '@features/onboarding/constants';
import {
  DependentSchema,
  dependentSchema,
} from '@features/onboarding/schema/onboarding';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';

import { Form } from '@components/ui/form';
import { Button } from '@components/ui/button';
import { LeftArrowIcon } from '@components/shared/icon/LeftArrowIcon';
import { RightArrowIcon } from '@components/shared/icon/RightArrowIcon';
import { cn } from '@utils/index';

interface NavigationButtonsProps {
  handleFormSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onChange?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onPrev?: (e?: React.BaseSyntheticEvent) => void;
  isFormValid?: boolean;
  isLoading?: boolean;
  containerClassName?: string;
  prevButtonClassName?: string;
  nextButtonClassName?: string;
  prevButtonTextClassName?: string;
  nextButtonTextClassName?: string;
  prevArrowDefaultClassName?: string;
  prevArrowHoverClassName?: string;
  nextArrowClassName?: string;
  isSubmitText?: boolean;
  hideNextButton?: boolean;
  nextButtonLabel?: string;
}

export const NavigationButtons = ({
  handleFormSubmit,
  onPrev,
  onChange,
  isFormValid,
  isSubmitText,
  isLoading = false,
  hideNextButton = false,
  containerClassName,
  prevButtonClassName,
  nextButtonClassName,
  prevButtonTextClassName,
  nextButtonTextClassName,
  prevArrowDefaultClassName,
  prevArrowHoverClassName,
  nextArrowClassName,
  nextButtonLabel,
}: NavigationButtonsProps) => {
  const data = useOnboardingStore((state) => state.data);
  const form = useForm<DependentSchema>({
    // @ts-ignore
    resolver: zodResolver(dependentSchema),
    defaultValues: {
      applicants: [DEFAULT_DEPENDENTS_FORM],
      ...data,
    },
  });

  const { currentFormStep, handlePreviousStep } = useFormControls();

  let formState;
  let handleSubmit;
  let isFormContextAvailable = true;

  try {
    ({ formState, handleSubmit } = useFormContext<OnboardingSchema>());
  } catch {
    isFormContextAvailable = false;
  }

  if (!currentFormStep) return null;

  const onPrevStep = () => {
    if (onPrev) {
      onPrev();
      return;
    }
    handlePreviousStep();
  };

  const onNextStep = async (e?: React.BaseSyntheticEvent) => {
    if (window.innerWidth <= 768) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    if (handleFormSubmit) {
      console.log('handleFormSubmit', e);
      await handleFormSubmit(e);
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex mt-2 gap-4 w-full max-lg:w-[88%] max-md:w-[85%] max-2xs:w-[81%]',
          containerClassName
        )}
        onChange={onChange}
      >
        <div className="flex-1">
          <Button
            type="button"
            className={cn(
              'group w-full flex items-center font-bold uppercase text-[18px] justify-start bg-transparent border border-white',
              'whitespace-nowrap hover:text-primary hover:bg-white transition-all disabled:pointer-events-none disabled:opacity-50',
              'cursor-pointer h-14 text-white rounded-4xl px-2',
              prevButtonClassName
            )}
            onClick={onPrevStep}
            disabled={isLoading}
          >
            <div className="flex items-center gap-2 mx-auto min-w-[calc(100%-8px)]">
              <div className="relative">
                <LeftArrowIcon
                  color="var(--color-card)"
                  className={cn(
                    'absolute inset-0 group-hover:opacity-0 transition-opacity max-2xl:hidden',
                    prevArrowDefaultClassName
                  )}
                />
                <LeftArrowIcon
                  color="var(--color-primary)"
                  className={cn(
                    '2xl:opacity-0 group-hover:opacity-100 transition-opacity',
                    prevArrowHoverClassName
                  )}
                />
              </div>
              <span
                className={cn('flex-1 text-center', prevButtonTextClassName)}
              >
                Previous
              </span>
            </div>
          </Button>
        </div>

        <div className="flex-1 flex items-end justify-end">
          {!hideNextButton ? (
            <Button
              type="button"
              className={cn(
                'group w-full font-bold uppercase text-[18px] flex items-center justify-end bg-white whitespace-nowrap hover:bg-primary hover:text-white 2xl:hover:bg-white 2xl:hover:text-primary transition-all disabled:pointer-events-none disabled:opacity-50 cursor-pointer h-14 text-primary rounded-4xl px-2',
                nextButtonClassName
              )}
              onClick={onNextStep}
              disabled={isLoading}
            >
              <div className="flex items-center gap-2 mx-auto min-w-[calc(100%-8px)]">
                <span
                  className={cn('flex-1 text-center', nextButtonTextClassName)}
                >
                  {isLoading
                    ? 'Processing...'
                    : (nextButtonLabel ?? (isSubmitText ? 'Submit' : 'Next'))}
                </span>
                <div className="relative w-3 h-4.5 flex items-center justify-center shrink-0">
                  <RightArrowIcon
                    color="var(--color-card)"
                    className="absolute inset-0 block 2xl:hidden"
                  />
                  <RightArrowIcon
                    color="var(--color-primary)"
                    className="absolute inset-0 hidden 2xl:block"
                  />
                </div>
              </div>
            </Button>
          ) : (
            <div className="w-full h-14" />
          )}
        </div>
      </form>
    </Form>
  );
};
